import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CommonTable from '../../../../components/common-table/CommonTable';
import UsersTableItem from './UsersTableItem';
import Tr from '../../../../components/common-table/components/Tr';
import Th from '../../../../components/common-table/components/Th';
import ConfirmationModal from '../../../../components/confirmation-modal/ConfirmationModal';
import DeleteLabel from '../../../../projects/projects-list/components/DeleteLabel';
import AssignProjectsModal from '../assign-projects-modal/AssignProjectsModal';

import scss from './UsersTable.module.scss';

const UsersTable = ({
  content = [],
  onRemoveUsers,
  onEnable,
  onAdminCheck,
  assignedCompanyProjects,
  onUsersChange,
}) => {
  const { t } = useTranslation('customers');
  const [selectedUser, setSelectedUser] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isChangeProjectsModalOpen, setIsChangeProjectsModalOpen] = useState(false);

  const isUsersTableLoading = useSelector((state) => state.customers.isUsersTableLoading);

  const formattedCompanyProjects = useMemo(
    () => assignedCompanyProjects.map((project) => ({ ...project, enabled: true })),
    [assignedCompanyProjects],
  );

  const handleOpenDeleteModal = useCallback((user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseModals = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleDeleteUser = useCallback(() => {
    onRemoveUsers(selectedUser);
    setSelectedUser({});
  }, [onRemoveUsers, selectedUser]);

  const handleOpenChangeProjectsModal = (user) => {
    setSelectedUser(user);
    setIsChangeProjectsModalOpen(true);
  };

  const handleChangeUserProjects = ({ id, checked }) => {
    if (checked) {
      setSelectedUser((user) => ({ ...user, assignedProjects: [...user.assignedProjects, id] }));
    }

    if (!checked) {
      setSelectedUser((user) => ({
        ...user,
        assignedProjects: user.assignedProjects.filter((projectId) => projectId !== id),
      }));
    }
  };

  const handleChangeProjectsModal = () => {
    onUsersChange(content.map((user) => (user.id === selectedUser.id ? selectedUser : user)));
    setSelectedUser({});
    setIsChangeProjectsModalOpen(false);
  };

  useEffect(() => {
    if (onUsersChange) {
      onUsersChange(
        content.map((user) => ({
          ...user,
          assignedProjects: user.assignedProjects.filter((projectId) =>
            formattedCompanyProjects.some(({ id }) => id === projectId),
          ),
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedCompanyProjects]);

  return (
    <>
      <CommonTable
        content={content}
        isLoading={isUsersTableLoading}
        renderBody={(item) => (
          <UsersTableItem
            key={item.id}
            user={item}
            onDelete={handleOpenDeleteModal}
            onEnable={onEnable}
            onAdminCheck={onAdminCheck}
            onChangeProjects={handleOpenChangeProjectsModal}
          />
        )}>
        <Tr>
          <Th isLeft>{t('user')}</Th>
          <Th isLeft>{t('fullName')}</Th>
          <Th isLeft extraClasses={scss.cellProjects}>
            {t('projects')}
          </Th>
          <Th extraClasses={scss.cellEnable}>{t('enabled')}</Th>
          <Th extraClasses={scss.cellEnable}>{t('companyAdmin')}</Th>
          <Th extraClasses={scss.cellActions}>{t('actions')}</Th>
        </Tr>
      </CommonTable>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModals}
        onCancel={handleCloseModals}
        onConfirm={handleDeleteUser}
        subLabel={<DeleteLabel name={selectedUser.username} />}
      />

      <AssignProjectsModal
        isOpen={isChangeProjectsModalOpen}
        companyProjects={formattedCompanyProjects}
        activeProjects={selectedUser.assignedProjects}
        onSelect={handleChangeUserProjects}
        onClose={handleChangeProjectsModal}
      />
    </>
  );
};

export default UsersTable;
